import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  buildStoreProductPath,
  consumeStorePaymentLinkReturn,
  fetchStoreCheckoutOrder,
  STORE_CHECKOUT_MODE_PAYMENT_LINK,
} from '../lib/store';

const TERMINAL_ORDER_STATUSES = new Set(['paid', 'failed', 'expired', 'canceled']);

const ORDER_STATUS_LABELS = {
  pending: 'Pending',
  paid: 'Paid',
  failed: 'Failed',
  expired: 'Expired',
  canceled: 'Canceled',
};

const ORDER_HEADLINES = {
  pending: 'Confirming your payment',
  paid: 'Order confirmed',
  failed: 'Payment failed',
  expired: 'Checkout expired',
  canceled: 'Checkout canceled',
};

const ORDER_MESSAGES = {
  pending:
    'Stripe sent you back to the store and we are waiting for the payment webhook to finalize the order. This usually clears within a few seconds.',
  paid: 'Your hosted checkout session is complete and the order has been recorded below.',
  failed:
    'Stripe could not complete this payment. Return to the product page to start a fresh checkout session.',
  expired:
    'This checkout session expired before payment completed. You can start again from the product page whenever you are ready.',
  canceled:
    'This checkout was canceled before payment completed. You can head back to the product page and try again.',
};

function formatTimestamp(value) {
  if (!value) {
    return '';
  }

  const timestamp = new Date(value);

  if (Number.isNaN(timestamp.getTime())) {
    return '';
  }

  return timestamp.toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function SummaryCard({ label, value }) {
  if (!value) {
    return null;
  }

  return (
    <div className="rounded-[22px] border border-slate-100 bg-slate-50/70 px-5 py-5">
      <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">{label}</p>
      <p className="mt-4 text-lg leading-7 text-slate-900">{value}</p>
    </div>
  );
}

function OrderSummary({ order }) {
  const createdAt = formatTimestamp(order.createdAt);
  const updatedAt = formatTimestamp(order.updatedAt);
  const paidAt = formatTimestamp(order.paidAt);
  const statusLabel = ORDER_STATUS_LABELS[order.status] || order.status;
  const timelineLabel = paidAt ? 'Paid' : updatedAt ? 'Updated' : createdAt ? 'Created' : '';
  const timelineValue = paidAt || updatedAt || createdAt;

  return (
    <div className="section-divider mt-8 grid gap-5 pt-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <SummaryCard label="Order" value={`#${order.id}`} />
        <SummaryCard label="Status" value={statusLabel} />
        <SummaryCard label="Email" value={order.customerEmail} />
        <SummaryCard label={timelineLabel} value={timelineValue} />
      </div>

      {order.lastError ? (
        <div className="rounded-[22px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm leading-7 text-rose-700">
          {order.lastError}
        </div>
      ) : null}

      {order.items.length > 0 ? (
        <div className="rounded-[28px] border border-slate-100 bg-white p-6">
          <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Items</p>
          <div className="mt-5 space-y-4">
            {order.items.map((item) => (
              <div
                key={item.id || `${item.productSlug}-${item.productId}`}
                className="flex flex-col gap-1 border-b border-slate-100 pb-4 last:border-b-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="text-base font-medium text-slate-900">{item.productName}</p>
                  <p className="mt-1 text-sm text-slate-600">
                    Qty {item.quantity} | {item.unitPriceDisplay} each
                  </p>
                </div>
                <p className="text-sm text-slate-900">{item.totalPriceDisplay}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="rounded-[28px] border border-slate-100 bg-white p-6">
        <p className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Totals</p>
        <div className="mt-5 space-y-3 text-sm text-slate-600">
          <div className="flex items-center justify-between gap-4">
            <span>Subtotal</span>
            <span className="text-slate-900">{order.totals.subtotalDisplay}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span>Tax</span>
            <span className="text-slate-900">{order.totals.taxDisplay}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span>Shipping</span>
            <span className="text-slate-900">{order.totals.shippingDisplay}</span>
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-3 text-base font-medium text-slate-900">
            <span>Total</span>
            <span>{order.totals.totalDisplay}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id') || '';
  const sessionId = searchParams.get('session_id') || '';
  const [paymentLinkReturn, setPaymentLinkReturn] = useState(null);
  const [paymentLinkReturnResolved, setPaymentLinkReturnResolved] = useState(Boolean(orderId || sessionId));

  useEffect(() => {
    if (orderId || sessionId) {
      setPaymentLinkReturnResolved(true);
      return;
    }

    setPaymentLinkReturn(consumeStorePaymentLinkReturn());
    setPaymentLinkReturnResolved(true);
  }, [orderId, sessionId]);

  const {
    data: order,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['checkoutOrder', orderId, sessionId],
    queryFn: () => fetchStoreCheckoutOrder({ orderId, sessionId }),
    enabled: Boolean(orderId && sessionId),
    refetchInterval: (query) => {
      const currentOrder = query.state.data;

      return TERMINAL_ORDER_STATUSES.has(currentOrder?.status) ? false : 2500;
    },
  });

  const hasTrackedCheckout = Boolean(orderId && sessionId);
  const hasPaymentLinkReturn = paymentLinkReturn?.checkoutMode === STORE_CHECKOUT_MODE_PAYMENT_LINK;
  const orderStatus = order?.status || '';
  const headline = hasPaymentLinkReturn
    ? 'Stripe checkout'
    : ORDER_HEADLINES[orderStatus] || 'Order received';
  const message =
    hasPaymentLinkReturn
      ? 'This product used a Stripe Payment Link, so this store does not have an internal order record to show here. If you completed payment on Stripe, your confirmation and receipt will come directly from Stripe.'
      : ORDER_MESSAGES[orderStatus] ||
        'Your secure checkout session has been recorded. Order details will appear here once they are available.';
  const retryProductSlug = order?.items?.[0]?.productSlug || paymentLinkReturn?.productSlug || '';
  const retryProductPath = retryProductSlug
    ? buildStoreProductPath({ slug: retryProductSlug })
    : '/store';
  const shouldOfferRetry = Boolean(
    order &&
      TERMINAL_ORDER_STATUSES.has(order.status) &&
      order.status !== 'paid' &&
      order.items?.[0]?.productSlug,
  );
  const paymentLinkProductName = paymentLinkReturn?.productName || '';

  return (
    <main className="page-shell">
      <div className="page-narrow surface-card px-8 py-10 md:px-10 md:py-12">
        <p className="eyebrow">Checkout</p>
        <h1 className="mt-5 text-4xl font-medium leading-tight text-slate-900 md:text-5xl">
          {headline}
        </h1>

        <p className="mt-5 max-w-2xl text-sm leading-7 text-slate-600 md:text-base">{message}</p>

        {hasPaymentLinkReturn ? (
          <div className="section-divider mt-8 grid gap-5 pt-8">
            {paymentLinkProductName ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <SummaryCard label="Product" value={paymentLinkProductName} />
              </div>
            ) : null}

            <div className="rounded-[22px] border border-slate-100 bg-slate-50/70 px-5 py-4 text-sm leading-7 text-slate-600">
              Payment Links bypass the app&apos;s order tracking, so there is no internal receipt or
              order timeline to render on this page.
            </div>
          </div>
        ) : !hasTrackedCheckout && !paymentLinkReturnResolved ? (
          <div className="mt-8 rounded-[22px] border border-slate-100 bg-slate-50/70 px-5 py-4 text-sm text-slate-600">
            Loading checkout details...
          </div>
        ) : !hasTrackedCheckout ? (
          <div className="mt-8 rounded-[22px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
            Missing checkout details. Return to the store and try again.
          </div>
        ) : isLoading ? (
          <div className="mt-8 rounded-[22px] border border-slate-100 bg-slate-50/70 px-5 py-4 text-sm text-slate-600">
            Loading order details...
          </div>
        ) : error ? (
          <div className="mt-8 rounded-[22px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
            {error.message || 'We could not load this order yet.'}
          </div>
        ) : order ? (
          <OrderSummary order={order} />
        ) : null}

        <div className="mt-8 flex flex-wrap gap-4">
          {shouldOfferRetry ? (
            <>
              <Link to={retryProductPath} className="primary-button">
                Return to product
              </Link>
              <Link to="/store" className="secondary-button">
                Browse store
              </Link>
            </>
          ) : hasPaymentLinkReturn ? (
            <>
              <Link to={retryProductPath} className="primary-button">
                {retryProductSlug ? 'Return to product' : 'Back to store'}
              </Link>
              <Link to="/contact" className="secondary-button">
                Contact Encreage
              </Link>
            </>
          ) : (
            <>
              <Link to="/store" className="primary-button">
                Back to store
              </Link>
              <Link to="/contact" className="secondary-button">
                Contact Encreage
              </Link>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
